'use client';

import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["videoProps", "onPlay", "onPause", "onClose", "closed", "subtitlesUrl", "videoPlayerSize", "portrait", "aspectRatio", "onMinimize", "onExpand", "alwaysHidePlayPauseButton", "ariaLabels", "onTimeUpdate", "hasOverlay", "unboundedAspectRatio"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useImperativeHandle } from 'react';
import { AdsBox } from "../../AdsBox/index.mjs";
import { useVideo } from "../../../hooks/useVideo.mjs";
import { ASPECT_RATIOS } from "../../../utils/config.mjs";
import { ButtonPrimary } from "../../../../core/components/ButtonPrimary/index.mjs";
import { ButtonTertiary } from "../../../../core/components/ButtonTertiary/index.mjs";
import { IconFullscreen } from "../../../../core/components/Icon/icons/IconFullscreen/index.mjs";
import { IconMinimize } from "../../../../core/components/Icon/icons/IconMinimize/index.mjs";
import { IconPause } from "../../../../core/components/Icon/icons/IconPause/index.mjs";
import { IconPlay } from "../../../../core/components/Icon/icons/IconPlay/index.mjs";
import { IconSubtitles } from "../../../../core/components/Icon/icons/IconSubtitles/index.mjs";
import { IconX } from "../../../../core/components/Icon/icons/IconX/index.mjs";
import { addColorSet } from "../../../../core/styles/index.mjs";
import { ecx } from "../../../../core/utils.mjs";
var VALID_ASPECT_RATIOS = [ASPECT_RATIOS.PORTRAIT, ASPECT_RATIOS.LANDSCAPE, ASPECT_RATIOS.SQUARE];
var DEFAULT_DIMENSIONS = _defineProperty(_defineProperty(_defineProperty({}, ASPECT_RATIOS.PORTRAIT, {
  sm: {
    width: 150,
    height: 264
  },
  lg: {
    width: 270,
    height: 480
  }
}), ASPECT_RATIOS.LANDSCAPE, {
  sm: {
    width: 264,
    height: 150
  },
  lg: {
    width: 480,
    height: 270
  }
}), ASPECT_RATIOS.SQUARE, {
  sm: {
    width: 225,
    height: 225
  },
  lg: {
    width: 405,
    height: 405
  }
});
var getVideoAspectRatio = function getVideoAspectRatio(aspectRatio, portrait) {
  if (aspectRatio && VALID_ASPECT_RATIOS.includes(aspectRatio)) return aspectRatio;

  // Once portrait prop is deprecated, default to landscape
  return !!portrait ? ASPECT_RATIOS.PORTRAIT : ASPECT_RATIOS.LANDSCAPE;
};
var parsePixelValue = function parsePixelValue(size) {
  if (!size) return undefined;
  var match = size.match(/^(\d+)px$/);
  return match ? parseInt(match[1], 10) : undefined;
};
var getVideoSize = function getVideoSize(_ref) {
  var width = _ref.width,
    portrait = _ref.portrait,
    aspectRatio = _ref.aspectRatio,
    dimension = _ref.dimension,
    videoPlayerSize = _ref.videoPlayerSize;
  var videoAspectRatio = getVideoAspectRatio(aspectRatio, portrait);

  // If the width is provided, either return it, or use it to calculate height
  if (dimension === 'width' && !!width) return width;
  if (dimension === 'height' && !!width) return width / videoAspectRatio;

  // If no width is provided, check the video size variant for default values
  if (dimension === 'width') {
    // Handles deprecated portrait prop
    if (portrait) return DEFAULT_DIMENSIONS[ASPECT_RATIOS.PORTRAIT][videoPlayerSize].width;
    return DEFAULT_DIMENSIONS[videoAspectRatio][videoPlayerSize].width;
  }
  // Handles deprecated portrait prop
  if (portrait) return DEFAULT_DIMENSIONS[ASPECT_RATIOS.PORTRAIT][videoPlayerSize].height;
  return DEFAULT_DIMENSIONS[videoAspectRatio][videoPlayerSize].height;
};
function maybeComposeContainerStyles(_ref2) {
  var width = _ref2.width,
    unboundedAspectRatio = _ref2.unboundedAspectRatio,
    videoPlayerSize = _ref2.videoPlayerSize,
    aspectRatio = _ref2.aspectRatio,
    portrait = _ref2.portrait;
  var styles = {};
  if (unboundedAspectRatio) {
    styles.width = 'initial';
    styles.height = 'initial';
    return styles;
  }
  var commonGetVideoSizeOpts = {
    width: width,
    videoPlayerSize: videoPlayerSize,
    aspectRatio: aspectRatio,
    portrait: portrait
  };
  styles.width = getVideoSize(_objectSpread(_objectSpread({}, commonGetVideoSizeOpts), {}, {
    dimension: 'width'
  }));
  var height = getVideoSize(_objectSpread(_objectSpread({}, commonGetVideoSizeOpts), {}, {
    dimension: 'height'
  }));
  styles.minHeight = height;
  styles.maxHeight = height;
  styles.height = height;
  return styles;
}
function Overlay(_ref3) {
  var show = _ref3.show,
    label = _ref3.label,
    onClick = _ref3.onClick;
  if (!show) {
    return null;
  }
  return /*#__PURE__*/React.createElement(ButtonTertiary, {
    "aria-label": label,
    "data-testid": "play-pause-overlay",
    className: ecx(['video-player-v2__overlay']),
    onClick: onClick
  });
}
function OverlayButton(_ref4) {
  var show = _ref4.show,
    button = _ref4.button,
    flexBasis = _ref4.flexBasis;
  if (!show) {
    return null;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: ecx(['video-player-v2__button']),
    style: {
      flexBasis: flexBasis
    }
  }, button);
}
function OverlayControls(_ref5) {
  var show = _ref5.show,
    flexBasis = _ref5.flexBasis,
    showSubtitles = _ref5.showSubtitles,
    subtitlesControl = _ref5.subtitlesControl,
    showMinimize = _ref5.showMinimize,
    minimizeControl = _ref5.minimizeControl,
    showClose = _ref5.showClose,
    closeControl = _ref5.closeControl;
  if (!show) {
    return null;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: ecx(['video-player-v2__options']),
    style: {
      flexBasis: flexBasis
    }
  }, showSubtitles && subtitlesControl, showMinimize && minimizeControl, showClose && closeControl);
}
function ProtectiveOverlay(_ref6) {
  var children = _ref6.children,
    hasGradient = _ref6.hasGradient,
    onMouseOver = _ref6.onMouseOver,
    onFocus = _ref6.onFocus,
    onMouseLeave = _ref6.onMouseLeave;
  var className = ecx(['video-player-v2__protective-overlay', {
    'video-player-v2__protective-overlay--gradient': hasGradient
  }, addColorSet('overMedia')]);
  return /*#__PURE__*/React.createElement("div", {
    className: className,
    onMouseOver: onMouseOver,
    onFocus: onFocus,
    onMouseLeave: onMouseLeave
  }, children);
}
var MOUSE_IDLE_HIDE_BUTTON_TIMEOUT = 3000;
var defaultAriaLabels = {
  subtitlesOffLabel: 'Subtitles off',
  subtitlesOnLabel: 'Subtitles on',
  playButtonLabel: 'Play',
  pauseButtonLabel: 'Pause',
  expandVideoLabel: 'Expand video',
  minimizeVideoLabel: 'Minimize video',
  closeButtonAriaLabel: 'Close'
};

/**
 * TODO(slavkob, 15/04/2025): this component needs to be moved out of it's nested dir once encore@10
 * is slated to be released. it doesn't work with the css build script and should follow a flatter
 * structure.
 */
export var VideoPlayer = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var videoProps = props.videoProps,
    onPlay = props.onPlay,
    onPause = props.onPause,
    onClose = props.onClose,
    closed = props.closed,
    subtitlesUrl = props.subtitlesUrl,
    _props$videoPlayerSiz = props.videoPlayerSize,
    videoPlayerSize = _props$videoPlayerSiz === void 0 ? 'lg' : _props$videoPlayerSiz,
    portrait = props.portrait,
    aspectRatio = props.aspectRatio,
    onMinimize = props.onMinimize,
    onExpand = props.onExpand,
    alwaysHidePlayPauseButton = props.alwaysHidePlayPauseButton,
    _props$ariaLabels = props.ariaLabels,
    ariaLabels = _props$ariaLabels === void 0 ? defaultAriaLabels : _props$ariaLabels,
    onTimeUpdate = props.onTimeUpdate,
    hasOverlay = props.hasOverlay,
    _props$unboundedAspec = props.unboundedAspectRatio,
    unboundedAspectRatio = _props$unboundedAspec === void 0 ? false : _props$unboundedAspec,
    rest = _objectWithoutProperties(props, _excluded);
  var subtitlesOffLabel = ariaLabels.subtitlesOffLabel,
    subtitlesOnLabel = ariaLabels.subtitlesOnLabel,
    playButtonLabel = ariaLabels.playButtonLabel,
    pauseButtonLabel = ariaLabels.pauseButtonLabel,
    expandVideoLabel = ariaLabels.expandVideoLabel,
    minimizeVideoLabel = ariaLabels.minimizeVideoLabel,
    closeButtonAriaLabel = ariaLabels.closeButtonAriaLabel;
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    showPlayPauseButton = _React$useState2[0],
    setShowPlayPauseButton = _React$useState2[1];
  var _React$useState3 = React.useState(videoPlayerSize),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    videoSize = _React$useState4[0],
    setVideoSize = _React$useState4[1];
  var _React$useState5 = React.useState(videoSize === 'sm'),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    videoMinimized = _React$useState6[0],
    setVideoMinimized = _React$useState6[1];
  var _React$useState7 = React.useState(false),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    show = _React$useState8[0],
    setShow = _React$useState8[1];
  var _React$useState9 = React.useState(!!subtitlesUrl),
    _React$useState0 = _slicedToArray(_React$useState9, 2),
    subtitlesOn = _React$useState0[0],
    setSubtitlesOn = _React$useState0[1];
  var _useVideo = useVideo(/*#__PURE__*/React.createElement("video", Object.assign({}, videoProps, {
      "data-testid": "video-element"
    }), /*#__PURE__*/React.createElement("track", {
      "default": true,
      src: subtitlesOn ? subtitlesUrl : '',
      kind: "captions",
      srcLang: "en"
    }))),
    _useVideo2 = _slicedToArray(_useVideo, 4),
    videoElement = _useVideo2[0],
    videoState = _useVideo2[1],
    videoControls = _useVideo2[2],
    videoRef = _useVideo2[3];
  var width = parsePixelValue(props.width);
  var height = getVideoSize({
    dimension: 'height',
    width: width,
    portrait: portrait,
    aspectRatio: aspectRatio,
    videoPlayerSize: videoSize
  });
  var flexBasis = height / 3;

  // The ProtectiveOverlay and PlayPauseButton should fade out if mouse has been idle for more than a few seconds
  // In order to do that, we need to store a timeout that restarts every time the mouse moves
  var mouseIdleTimeout = React.useRef();
  useEffect(function () {
    if (!videoRef.current) return undefined;
    var videoElementRef = videoRef.current;
    var handleTimeUpdate = function handleTimeUpdate() {
      onTimeUpdate === null || onTimeUpdate === void 0 || onTimeUpdate(videoElementRef.currentTime);
    };
    videoElementRef.addEventListener('timeupdate', handleTimeUpdate);
    return function () {
      videoElementRef.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [onTimeUpdate, videoRef]);
  useEffect(function () {
    // clear timeout on component unmount
    return function () {
      if (mouseIdleTimeout.current) clearTimeout(mouseIdleTimeout.current);
    };
  }, []);
  useEffect(function () {
    setShow(!!closed);
  }, [closed]);
  useImperativeHandle(ref, function () {
    return videoRef.current;
  }, [videoRef]);
  var styles = maybeComposeContainerStyles({
    width: width,
    unboundedAspectRatio: unboundedAspectRatio,
    videoPlayerSize: videoSize,
    aspectRatio: aspectRatio,
    portrait: portrait
  });
  function onOverlayClick() {
    if (videoState.paused) {
      videoControls.play();
      onPlay === null || onPlay === void 0 || onPlay();
      return;
    }
    videoControls.pause();
    onPause === null || onPause === void 0 || onPause();
  }
  var overlayLabel = videoState.paused ? playButtonLabel : pauseButtonLabel;
  var overlay = /*#__PURE__*/React.createElement(Overlay, {
    show: alwaysHidePlayPauseButton,
    label: overlayLabel,
    onClick: onOverlayClick
  });
  var showButton = (videoState.paused || showPlayPauseButton) && !alwaysHidePlayPauseButton;
  var buttonLabel = videoState.paused ? playButtonLabel : pauseButtonLabel;
  var buttonIcon = videoState.paused ? IconPlay : IconPause;
  function onButtonClick() {
    if (videoState.paused) {
      videoControls.play();
      onPlay === null || onPlay === void 0 || onPlay();
      return;
    }
    videoControls.pause();
    onPause === null || onPause === void 0 || onPause();
  }
  var buttonEl = /*#__PURE__*/React.createElement(ButtonPrimary, {
    "data-testid": "play-pause-button",
    colorSet: "overMedia",
    size: "medium",
    "aria-label": buttonLabel,
    iconOnly: buttonIcon,
    onClick: onButtonClick
  });
  var button = /*#__PURE__*/React.createElement(OverlayButton, {
    show: showButton,
    button: buttonEl,
    flexBasis: flexBasis
  });
  var emptyButton = /*#__PURE__*/React.createElement(OverlayButton, {
    show: showButton,
    flexBasis: flexBasis
  });
  var showControls = videoState.paused || showPlayPauseButton;
  var showClose = !!onClose;
  var showSubtitles = !!subtitlesUrl;
  var subtitlesLabel = subtitlesOn ? subtitlesOnLabel : subtitlesOffLabel;
  var onSubtitlesClick = function onSubtitlesClick() {
    return setSubtitlesOn(!subtitlesOn);
  };
  var subtitlesControl = /*#__PURE__*/React.createElement(ButtonPrimary, {
    size: "small",
    "aria-label": subtitlesLabel,
    iconOnly: IconSubtitles,
    colorSet: "overMedia",
    onClick: onSubtitlesClick
  });
  function onMinimizeClick() {
    if (onExpand && videoMinimized) {
      setVideoMinimized(false);
      onExpand();
      return;
    }
    if (onMinimize && !videoMinimized) {
      setVideoMinimized(true);
      onMinimize();
      return;
    }
    setVideoMinimized(videoSize !== 'sm');
    setVideoSize(videoSize === 'sm' ? 'lg' : 'sm');
  }
  var showMinimize = !width || !!onMinimize;
  var minimizeLabel = videoMinimized ? expandVideoLabel : minimizeVideoLabel;
  var minimizeIcon = videoMinimized ? IconFullscreen : IconMinimize;
  var minimizeControl = /*#__PURE__*/React.createElement(ButtonPrimary, {
    size: "small",
    "aria-label": minimizeLabel,
    iconOnly: minimizeIcon,
    colorSet: "overMedia",
    onClick: onMinimizeClick
  });
  function onCloseClick() {
    setShow(true);
    onClose === null || onClose === void 0 || onClose(true);
  }
  var closeControl = /*#__PURE__*/React.createElement(ButtonPrimary, {
    size: "small",
    "aria-label": closeButtonAriaLabel,
    iconOnly: IconX,
    colorSet: "overMedia",
    onClick: onCloseClick
  });
  var controls = /*#__PURE__*/React.createElement(OverlayControls, {
    show: showControls,
    flexBasis: flexBasis,
    showSubtitles: showSubtitles,
    subtitlesControl: subtitlesControl,
    showMinimize: showMinimize,
    minimizeControl: minimizeControl,
    showClose: showClose,
    closeControl: closeControl
  });
  function onProtectiveOverlayMouseEvent() {
    setShowPlayPauseButton(true);

    // Restart the mouseIdleTimeout whenever the mouse moves.
    if (mouseIdleTimeout.current) {
      clearTimeout(mouseIdleTimeout.current);
    }
    mouseIdleTimeout.current = setTimeout(function () {
      return setShowPlayPauseButton(false);
    }, MOUSE_IDLE_HIDE_BUTTON_TIMEOUT);
  }
  function onProtectiveOverlayMouseOver(event) {
    event.preventDefault();
    onProtectiveOverlayMouseEvent();
  }
  function onProtectiveOverlayFocus(event) {
    event.preventDefault();
    onProtectiveOverlayMouseEvent();
  }
  var onProtectiveOverlayMouseLeave = function onProtectiveOverlayMouseLeave() {
    return setShowPlayPauseButton(false);
  };
  var protectiveOverlay = /*#__PURE__*/React.createElement(ProtectiveOverlay, {
    onMouseOver: onProtectiveOverlayMouseOver,
    onMouseLeave: onProtectiveOverlayMouseLeave,
    onFocus: onProtectiveOverlayFocus,
    hasGradient: hasOverlay
  }, overlay, controls, button, emptyButton);
  return /*#__PURE__*/React.createElement(React.Fragment, null, !show && /*#__PURE__*/React.createElement(AdsBox, Object.assign({
    "data-testid": "video-container"
  }, rest, {
    className: ecx(['video-player-v2', addColorSet('invertedDark')], props.className),
    style: styles
  }), protectiveOverlay, videoElement));
});